import { useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useDeleteDocument,
  useDocuments,
  useKnowledgeBase,
  useUploadDocument,
} from '@/lib/api/knowledge';
import { ApiError } from '@/lib/api/client';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { QueryState } from '@/components/QueryState';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { cn } from '@/components/ui/cn';
import { useWorkspacePath } from '@/lib/workspacePath';
import type { DocumentStatus, KnowledgeDocument } from '@/lib/api/types';

const STATUS_BADGE: Record<DocumentStatus, string> = {
  pending: 'bg-gray-100 text-gray-700',
  processing: 'bg-brand-50 text-brand-700',
  ready: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-700',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(1)} ${units[i]}`;
}

/**
 * One knowledge base: its document table (with status badges and failure
 * reasons, polling while ingestion runs) plus the upload form and per-document
 * delete (spec 0014, AC-13). The URL addresses the base by slug; a UUID also
 * resolves (0013 policy).
 */
export function KnowledgeBaseDetailPage() {
  const { baseRef } = useParams<{ baseRef: string }>();
  const ws = useWorkspacePath();

  const base = useKnowledgeBase(baseRef);
  const documents = useDocuments(baseRef);
  const upload = useUploadDocument(baseRef ?? '');
  const deleteDocument = useDeleteDocument(baseRef ?? '');

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selected, setSelected] = useState<File | null>(null);

  function handleUpload(e: FormEvent) {
    e.preventDefault();
    if (!selected) return;
    upload.mutate(selected, {
      onSuccess: () => {
        setSelected(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });
  }

  function handleDelete(doc: KnowledgeDocument) {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    deleteDocument.mutate(doc.id);
  }

  if (base.isLoading) {
    return (
      <div className="py-10">
        <Spinner label="Loading knowledge base" />
      </div>
    );
  }

  // An unknown slug/UUID under the active workspace is a not-found state; the
  // resolution stays inside the workspace RLS session (spec 0013 pattern).
  if (base.error || !base.data) {
    const notFound = base.error instanceof ApiError && base.error.status === 404;
    return (
      <div
        role="alert"
        data-testid="knowledge-base-not-found"
        className="rounded-md border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700"
      >
        <p>{notFound ? 'Knowledge base not found.' : 'Failed to load this knowledge base.'}</p>
        <Link
          to={ws('knowledge')}
          className="mt-2 inline-block font-medium text-brand-700 hover:underline"
        >
          Back to knowledge bases
        </Link>
      </div>
    );
  }

  const processingCount = (documents.data ?? []).filter(
    (doc) => doc.status === 'pending' || doc.status === 'processing',
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold text-gray-900">{base.data.name}</h1>
          {base.data.description && (
            <p className="truncate text-sm text-gray-500">{base.data.description}</p>
          )}
        </div>
        <Link to={ws('knowledge')} className="shrink-0 text-sm text-brand-600 hover:underline">
          Back to knowledge bases
        </Link>
      </div>

      {/* Announce ingestion progress to assistive tech as statuses change. */}
      <p aria-live="polite" className="sr-only" data-testid="knowledge-processing-announcer">
        {processingCount > 0 ? `${processingCount} document(s) still processing` : ''}
      </p>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card
            title="Documents"
            description="Uploaded files are processed in the background; status updates automatically."
          >
            <QueryState
              isLoading={documents.isLoading}
              error={documents.error}
              isEmpty={!documents.data || documents.data.length === 0}
              emptyMessage="No documents yet. Upload one to start grounding agents."
              loadingLabel="Loading documents"
              skeleton={<SkeletonRows rows={4} />}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="document-table">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-600">
                      <th className="py-2 pr-4 font-medium">Name</th>
                      <th className="py-2 pr-4 font-medium">Format</th>
                      <th className="py-2 pr-4 font-medium">Size</th>
                      <th className="py-2 pr-4 font-medium">Chunks</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 font-medium">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {documents.data?.map((doc) => (
                      <tr key={doc.id} data-testid="document-row">
                        <td className="max-w-xs truncate py-2 pr-4 text-gray-900">{doc.name}</td>
                        <td className="py-2 pr-4 text-gray-500">{doc.format}</td>
                        <td className="py-2 pr-4 text-gray-500">{formatBytes(doc.size_bytes)}</td>
                        <td className="py-2 pr-4 text-gray-500">{doc.chunk_count ?? '-'}</td>
                        <td className="py-2 pr-4">
                          <span
                            className={cn(
                              'inline-block rounded-full px-2 py-0.5 text-xs',
                              STATUS_BADGE[doc.status],
                            )}
                            data-testid="document-status"
                          >
                            {doc.status}
                          </span>
                          {doc.status === 'failed' && doc.error && (
                            <p
                              className="mt-1 max-w-xs text-xs text-red-700"
                              data-testid="document-error"
                            >
                              {doc.error}
                            </p>
                          )}
                        </td>
                        <td className="py-2 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(doc)}
                            disabled={deleteDocument.isPending}
                            data-testid="delete-document-button"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </QueryState>

            {deleteDocument.error && (
              <p
                role="alert"
                className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {(deleteDocument.error as Error).message}
              </p>
            )}
          </Card>
        </div>

        <div>
          <Card title="Upload document" description="PDF, DOCX, HTML, TXT, or MD.">
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label
                  htmlFor="knowledge-file-input"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  File
                </label>
                <input
                  ref={fileInputRef}
                  id="knowledge-file-input"
                  type="file"
                  accept=".pdf,.docx,.html,.htm,.txt,.md"
                  onChange={(e) => setSelected(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100"
                  data-testid="knowledge-file-input"
                />
              </div>

              {upload.error && (
                <p
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                >
                  {(upload.error as Error).message}
                </p>
              )}

              <Button
                type="submit"
                disabled={!selected || upload.isPending}
                className="w-full"
                data-testid="upload-document-button"
              >
                {upload.isPending ? 'Uploading...' : 'Upload'}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
